import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import { Modal, Row, Col, Card, Button, Spinner, Container } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { selectCurrentUser } from '@awp/stores/usersSlice';
import { FormInput, FormDropZone } from '@components/Common/AwpFormInputs';
import AwpYesNoDialog from '@awp/components/Common/AwpYesNoDialog';
import * as Yup from 'yup';
import { externalObject, bytesToMB, maxFileSize } from './helpers';

async function uploadFile({ eventId, fileId, source, description, files }) {
  const formData = new FormData();
  formData.append('eventId', eventId);
  if (fileId) formData.append('fileId', fileId);
  formData.append('source', source);
  formData.append('description', description);
  files.forEach((file) => formData.append('files', file));

  const result = await fetch('/api/v1/files/upload', {
    method: 'POST',
    body: formData,
  })
    .then(async (resp) => {
      if (!resp.ok) {
        throw new Error('Network error');
      }

      return { status: true, data: await resp.json() };
    })
    .catch((err) => ({ status: false, message: err.message || err, data: [] }));

  return result;
}

async function downloadFile({ eventId, fileId }) {
  const formData = new FormData();
  formData.append('eventId', eventId);
  formData.append('fileId', fileId);

  const result = await fetch('/api/v1/files/download', {
    method: 'POST',
    body: formData,
  })
    .then(async (resp) => {
      if (!resp.ok) {
        throw new Error('Network error');
      }

      return { status: true, data: await resp.blob() };
    })
    .catch((err) => ({ status: false, message: err.message || err, data: null }));

  return result;
}

function EntityModal({ eventId, data = {}, targetSize = maxFileSize, mode, show, onClose }) {
  const currentUser = useSelector(selectCurrentUser);
  const [showSpinner, setShowSpinner] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');

  const hasReadPermission = currentUser.Permissions.includes('READ_AVALANCHE_HAZARD');
  const hasWritePermission = currentUser.Permissions.includes('WRITE_AVALANCHE_HAZARD');

  const handleSubmit = async (values, formikHelpers) => {
    setUploadMessage('');
    setShowSpinner(true);
    const { status, data: info } = await uploadFile({ ...values, eventId, fileId: data.EventExternalObjectId });

    if (status) {
      if (info.length === 0) {
        toast.success('3rd party forecast successfully uploaded.');
        onClose();
        return;
      }

      const { Status: uploadStatus, Message: uploadMessage } = info[0];

      if (uploadStatus) {
        toast.success('3rd party forecast successfully uploaded.');
        onClose();
      } else {
        toast.error('3rd party forecast upload failed!', {
          autoClose: false,
        });

        if (uploadMessage === 'virus detected') {
          setUploadMessage('We found a virus in the uploaded file.');
        } else if (uploadMessage === 'upload failed') {
          setUploadMessage('The file failed to be stored.');
        } else {
          setUploadMessage(uploadMessage);
        }
      }
    } else {
      toast.error('3rd party forecast upload failed!', {
        autoClose: false,
      });
    }
    setShowSpinner(false);
  };

  const readonly = mode === 'view';

  if (!hasReadPermission) return null;
  if (!hasWritePermission) mode = 'view';

  return (
    <Modal
      show={show}
      onHide={onClose}
      backdrop="static"
      size="md"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Formik
        enableReinitialize
        validateOnMount
        initialValues={{
          source: data.SourceDescription,
          description: data.RelevanceDescription,
          files: [],
        }}
        validationSchema={Yup.object({
          source: Yup.string().max(50, 'Maximum 50 characters are allowed').required('Source is a required field'),
          description: Yup.string()
            .max(100, 'Maximum 100 characters are allowed')
            .required('Description is a required field'),
          files: mode === 'add' ? Yup.array().min(1) : null,
        })}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit }) => (
          <>
            <Modal.Header>
              <Modal.Title>
                <h3 className="awp-card-header">
                  {mode === 'add' ? 'Upload' : mode === 'view' ? 'View' : 'Edit'} 3rd Party Forecast
                </h3>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {showSpinner ? (
                <div className="my-5 text-center">
                  <Spinner as="span" animation="border" size="md" role="status" aria-hidden="true" />
                </div>
              ) : (
                <>
                  <Form>
                    <FormInput label="Source" name="source" id="source" disabled={readonly} />
                    <FormInput label="Description" name="description" id="description" disabled={readonly} />
                    {!readonly && (
                      <FormDropZone
                        label={`Upload Document (Max. ${bytesToMB(targetSize)} MB)`}
                        name="files"
                        id="files"
                        maxSize={targetSize}
                        // See https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
                        // accept={{
                        //   'application/pdf': ['.pdf'],
                        //   'image/tiff': ['.tif', '.tiff'],
                        //   'image/jpeg': ['.jpeg', '.jpg'],
                        //   'image/gif': ['.gif'],
                        //   'image/png': ['.png'],
                        //   'image/bmp': ['.bmp'],
                        // }}
                        onChange={() => setUploadMessage('')}
                      />
                    )}
                  </Form>
                  {uploadMessage && (
                    <div className="text-danger mt-1">
                      <ul>
                        <li>{uploadMessage}</li>
                      </ul>
                    </div>
                  )}
                </>
              )}
            </Modal.Body>
            <Modal.Footer className="justify-content-start">
              {showCancelConfirm ? (
                <AwpYesNoDialog
                  action={() => {
                    setShowCancelConfirm(false);
                    onClose();
                  }}
                  setShowYesNoConfirm={setShowCancelConfirm}
                  msg={'Are you sure you want to close?'}
                  props={{}}
                />
              ) : (
                <>
                  <Button className="" variant="outline-primary" outline onClick={() => setShowCancelConfirm(true)}>
                    Cancel
                  </Button>
                  {readonly ? (
                    <Button
                      type="button"
                      className=""
                      variant="primary"
                      onClick={async () => {
                        const { status, data: blob } = await downloadFile({
                          eventId,
                          fileId: data.EventExternalObjectId,
                        });
                        if (status) {
                          const a = document.createElement('a');
                          a.href = window.URL.createObjectURL(blob);
                          a.download = data.OriginalObjectName;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                        }
                      }}
                    >
                      Download
                    </Button>
                  ) : showSpinner ? (
                    <></>
                  ) : (
                    <Button className="" variant="primary" onClick={handleSubmit}>
                      Upload
                    </Button>
                  )}
                </>
              )}
            </Modal.Footer>
          </>
        )}
      </Formik>
    </Modal>
  );
}

EntityModal.propTypes = {
  eventId: PropTypes.number,
  data: externalObject,
  mode: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
};

export default EntityModal;
